import { ApiError, type EventData } from "@agentsaas/protocol";
import type { ApiClient } from "@agentsaas/client-sdk";

/**
 * 某个 agent 电脑上"已打开文件"的状态（与 React 无关，便于测试）：
 * - 打开：fsRead 拿内容与 etag
 * - 保存：fsWrite 带 If-Match；412 → 进入冲突，调用方弹出三选一（覆盖 / 放弃 / 看 diff）
 * - fs.changed：未修改的文件自动更新为最新版；有未保存修改的文件标记为"外部已修改"
 */

export interface OpenFile {
  path: string;
  name: string;
  loading: boolean;
  error: ApiError | null;
  /** 服务端版本（最近一次读取 / 保存成功的内容） */
  content: string;
  etag: string;
  /** 编辑中的内容 */
  draft: string;
  /** 不可编辑：二进制或需要下载 */
  binary: { url?: string; size?: number; /** 内联的二进制内容（fsRead encoding=base64），模型文件用它在浏览器里直接打开 */ base64?: string } | null;
  view: "edit" | "diff";
  saving: boolean;
  /** 保存时遇到的服务端新版本（412） */
  conflict: { content: string; etag: string } | null;
  /** 外部修改（agent 或他人） */
  external: { content: string; etag: string } | "deleted" | null;
}

export const isDirty = (f: OpenFile) => !f.binary && f.draft !== f.content;
export const baseName = (p: string) => p.split("/").pop() ?? p;

export type SaveResult = "saved" | "conflict";
export type FilesEvent = { type: "reloaded"; path: string } | { type: "saved"; path: string };

export class FilesStore {
  private files = new Map<string, OpenFile>();
  private order: string[] = [];
  private active: string | null = null;
  private listeners = new Set<() => void>();
  private eventListeners = new Set<(e: FilesEvent) => void>();
  /** 自己刚保存的文件：忽略随后到来的 by=user 的 fs.changed */
  private recentSaves = new Map<string, number>();
  private snapshot: { files: OpenFile[]; active: string | null } = { files: [], active: null };

  constructor(
    private readonly api: ApiClient,
    private readonly aid: string,
    private readonly now: () => number = Date.now,
  ) {}

  // ── 订阅（useSyncExternalStore） ──
  subscribe = (cb: () => void) => {
    this.listeners.add(cb);
    return () => void this.listeners.delete(cb);
  };
  getSnapshot = () => this.snapshot;
  onEvent(cb: (e: FilesEvent) => void): () => void {
    this.eventListeners.add(cb);
    return () => void this.eventListeners.delete(cb);
  }

  private emit() {
    this.snapshot = { files: this.order.map((p) => this.files.get(p)!), active: this.active };
    for (const l of this.listeners) l();
  }
  private patch(path: string, p: Partial<OpenFile>) {
    const f = this.files.get(path);
    if (!f) return;
    this.files.set(path, { ...f, ...p });
    this.emit();
  }
  get(path: string) {
    return this.files.get(path);
  }

  // ── 打开 / 关闭 / 切换 ──
  async open(path: string) {
    this.active = path;
    if (this.files.has(path)) {
      this.emit();
      return;
    }
    this.files.set(path, {
      path,
      name: baseName(path),
      loading: true,
      error: null,
      content: "",
      etag: "",
      draft: "",
      binary: null,
      view: "edit",
      saving: false,
      conflict: null,
      external: null,
    });
    this.order.push(path);
    this.emit();
    try {
      const latest = await this.read(path);
      this.patch(path, { loading: false, ...latest, draft: latest.content });
    } catch (e) {
      this.patch(path, { loading: false, error: e instanceof ApiError ? e : new ApiError("common.internal") });
    }
  }

  close(path: string) {
    if (!this.files.delete(path)) return;
    const i = this.order.indexOf(path);
    this.order.splice(i, 1);
    if (this.active === path) this.active = this.order[Math.min(i, this.order.length - 1)] ?? null;
    this.emit();
  }

  activate(path: string | null) {
    this.active = path;
    this.emit();
  }

  setDraft(path: string, draft: string) {
    this.patch(path, { draft });
  }

  setView(path: string, view: OpenFile["view"]) {
    this.patch(path, { view });
  }

  private async read(path: string): Promise<Pick<OpenFile, "content" | "etag" | "binary">> {
    const r = await this.api.call("fsRead", { params: { aid: this.aid }, query: { path } });
    if (r.kind === "download") return { content: "", etag: r.etag, binary: { url: r.url, size: r.size } };
    if (r.encoding === "base64") return { content: "", etag: r.etag, binary: { base64: r.content } };
    return { content: r.content, etag: r.etag, binary: null };
  }

  // ── 保存 ──
  async save(path: string, opts: { force?: boolean } = {}): Promise<SaveResult> {
    const f = this.files.get(path);
    if (!f || f.binary) return "saved";
    const draft = f.draft;
    const etag = opts.force && f.conflict ? f.conflict.etag : f.etag;
    this.patch(path, { saving: true });
    try {
      this.recentSaves.set(path, this.now());
      const r = await this.api.call("fsWrite", { params: { aid: this.aid }, query: { path }, body: { content: draft } }, { ifMatch: etag || undefined });
      this.patch(path, { saving: false, content: draft, etag: r.etag, conflict: null, external: null, view: "edit" });
      for (const l of this.eventListeners) l({ type: "saved", path });
      return "saved";
    } catch (e) {
      this.patch(path, { saving: false });
      if (e instanceof ApiError && e.code === "common.precondition_failed") {
        const latest = await this.read(path);
        this.patch(path, { conflict: { content: latest.content, etag: latest.etag } });
        return "conflict";
      }
      throw e;
    }
  }

  /** 冲突三选一 */
  async resolveConflict(path: string, choice: "overwrite" | "discard" | "diff") {
    const f = this.files.get(path);
    if (!f?.conflict) return;
    if (choice === "overwrite") {
      await this.save(path, { force: true });
    } else if (choice === "discard") {
      this.patch(path, { content: f.conflict.content, draft: f.conflict.content, etag: f.conflict.etag, conflict: null, external: null, view: "edit" });
    } else {
      // 以服务端最新版为基准展示 diff；用户确认后再保存即为覆盖
      this.patch(path, { content: f.conflict.content, etag: f.conflict.etag, conflict: null, external: null, view: "diff" });
    }
  }

  // ── 外部修改 ──
  async onFsChanged(e: EventData<"fs.changed">) {
    const f = this.files.get(e.path);
    if (!f) return;
    const saved = this.recentSaves.get(e.path);
    if (e.by === "user" && saved !== undefined && this.now() - saved < 3000) return;
    if (e.change === "deleted") {
      this.patch(e.path, { external: "deleted" });
      return;
    }
    let latest: Awaited<ReturnType<FilesStore["read"]>>;
    try {
      latest = await this.read(e.path);
    } catch {
      return;
    }
    const cur = this.files.get(e.path);
    if (!cur || latest.etag === cur.etag) return;
    if (!isDirty(cur)) {
      this.patch(e.path, { ...latest, draft: latest.content, external: null });
      for (const l of this.eventListeners) l({ type: "reloaded", path: e.path });
    } else {
      this.patch(e.path, { external: { content: latest.content, etag: latest.etag } });
    }
  }

  /** 丢弃本地修改，加载外部最新版 */
  acceptExternal(path: string) {
    const f = this.files.get(path);
    if (!f?.external || f.external === "deleted") return;
    this.patch(path, { content: f.external.content, draft: f.external.content, etag: f.external.etag, external: null, view: "edit" });
  }

  /** 以外部最新版为基准对比本地修改 */
  compareExternal(path: string) {
    const f = this.files.get(path);
    if (!f?.external || f.external === "deleted") return;
    this.patch(path, { content: f.external.content, etag: f.external.etag, external: null, view: "diff" });
  }
}
